import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DbNewsItem } from "../../../lib/supabase";
import { fetchNewsItems, getLang } from "../../../lib/newsUtils";

const CATEGORY_COLORS: Record<string, string> = {
  "grant-call": "text-gold border-gold",
  news: "text-navy border-navy",
  event: "text-emerald-600 border-emerald-400",
  result: "text-navy/50 border-cool-gray-300",
};

export default function NewsPreview() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [newsItems, setNewsItems] = useState<DbNewsItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const load = async () => {
      const lang = getLang(i18n.language);
      const items = await fetchNewsItems({ lang, publishedOnly: true, limit: 4 });
      setNewsItems(items);
      setTotalCount(items.length);
    };
    load();
  }, [i18n.language]);

  const featured = newsItems[0];
  const side = newsItems.slice(1, 4);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  if (!featured) {
    return (
      <section className="bg-cool-gray-50 py-20 md:py-28" ref={ref}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center py-12 text-navy/30">
          <i className="ri-newspaper-line text-3xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-cool-gray-50 py-20 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-3">
              {t("newsPreview.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-navy leading-snug">
              {t("newsPreview.title")}
            </h2>
          </div>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-navy font-semibold text-sm hover:text-gold transition-colors duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            {t("newsPreview.viewAll")}
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-base" />
            </span>
          </Link>
        </div>

        <hr className="border-cool-gray-200 mb-12" />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Featured article */}
          <div
            className={`lg:col-span-3 group transition-all duration-700 delay-100 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <Link to={`/news/${featured.slug}`} className="block cursor-pointer">
              <div className="relative w-full h-64 md:h-72 overflow-hidden rounded-lg mb-6">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent rounded-lg" />
                <div className="absolute top-4 left-4">
                  <span className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-gold/90 text-white">
                    {featured.category_label}
                  </span>
                </div>
              </div>
              {featured.deadline && (
                <p className="text-xs text-red-600 font-medium flex items-center gap-1 mb-2">
                  <i className="ri-alarm-line" />
                  {t("newsPreview.deadline")} {formatDate(featured.deadline)}
                </p>
              )}
              <h3 className="text-xl md:text-2xl font-bold text-navy leading-snug mb-3 group-hover:text-navy-light transition-colors">
                {featured.title}
              </h3>
              <p className="text-sm text-navy/55 leading-relaxed mb-4">{featured.excerpt}</p>
              <span className="text-xs text-navy/35">
                {formatDate(featured.date)}
                {featured.source ? ` · ${featured.source}` : ""}
              </span>
            </Link>
          </div>

          {/* Side list */}
          <div className="lg:col-span-2 flex flex-col divide-y divide-cool-gray-200">
            {side.map((item, i) => (
              <Link
                to={`/news/${item.slug}`}
                key={item.id}
                className={`group py-6 first:pt-0 last:pb-0 block cursor-pointer transition-all duration-700 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${(i + 1) * 100 + 100}ms` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`text-xs font-semibold uppercase tracking-wider border-b pb-px ${CATEGORY_COLORS[item.category]}`}
                  >
                    {item.category_label}
                  </span>
                  {item.deadline && (
                    <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                      <i className="ri-time-line text-xs" />
                      {formatDate(item.deadline)}
                    </span>
                  )}
                </div>
                <h4 className="text-sm font-semibold text-navy leading-snug mb-2 group-hover:text-gold transition-colors line-clamp-2">
                  {item.title}
                </h4>
                <span className="text-xs text-navy/35">{formatDate(item.date)}</span>
              </Link>
            ))}

            <div className="pt-6">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-xs font-semibold text-gold hover:text-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-news-line text-sm" />
                </span>
                {t("newsPreview.seeAll", { count: totalCount })}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
